import { create } from "zustand";
import { persist, createJSONStorage, combine } from "zustand/middleware";
import isEqual from "lodash/isEqual";
import { defaultFilters, countrySelection, typeSelection } from "@/lib/shared/constants";
import { sendGtagEvent } from "@/lib/client/gtag";
import { isValidActiveFilters } from "@/lib/client/filters";

// Re-export types for convenience
export type { ActiveFilters } from "@/types/filters.d.ts";

type ActiveFiltersWithId = ActiveFilters & { id?: number };

/**
 * Initial state for the filters store.
 * Contains active search filters, search text, saved filters, and UI preferences.
 */
const initialState = {
  /** Currently active filter selections */
  activeFilters: defaultFilters as unknown as ActiveFilters,
  /** Free-form search text */
  searchText: "",
  /** ID of the currently selected saved filter */
  savedFilterId: undefined as number | undefined,
  /** User's saved filters from the database */
  savedFilters: [] as DBSavedFilter[],
  /** Whether to exclude relisted items from search results */
  excludeRelists: false,
  /** Whether to exclude auction items from search results */
  excludeAuctions: false,
  /** Number of days back to limit search (1-90, or undefined for all time) */
  daysBack: undefined as number | undefined,
  /** Whether Kurb Lens (reverse image search) is currently active */
  isImageSearchActive: false,
  /** Whether KURBO mode (last-hour real-time listings) is active */
  kurboMode: false,
  /**
   * True only while the user is in the same session that created the current saved filter.
   * Auto-save of filter changes is allowed when true; requires "SAVE CHANGES" when false.
   * Not persisted — resets to false on every page load.
   */
  isNewFilter: false,
};

/**
 * Filters store manages search filters and saved filters.
 *
 * Features:
 * - Active filter management (brands, vendors, regions, price, etc.)
 * - Saved filters (create, update, delete, select)
 * - URL-based filter preloading
 * - Sync to API for authenticated users
 * - Google Analytics event tracking
 *
 * Persistence:
 * - Active filters, selected saved filter id, search text, excludeRelists, and daysBack are persisted to localStorage
 * - Saved filters are loaded from the API/database
 *
 * @example
 * ```ts
 * const { actions } = useFiltersStore();
 * actions.updateActiveFilter("brands", newSelection, user);
 * actions.saveFilters("My Filter", undefined, savedFilters, saveFn, updateFn);
 * ```
 */
export const useFiltersStore = create(
  persist(
    combine(initialState, (set, get) => {
      const actions = {
        /** Sets whether to exclude relisted items from search results */
        setExcludeRelists: (exclude: boolean) => set({ excludeRelists: exclude }),

        /** Sets whether to exclude auction items from search results */
        setExcludeAuctions: (exclude: boolean) => set({ excludeAuctions: exclude }),

        /** Enables or disables KURBO mode (1-hour time filter for latest drops) */
        setKurboMode: (enabled: boolean) => set({ kurboMode: enabled }),

        /**
         * Sets the days back filter value.
         * Values are clamped to 1-90 range. Undefined or 0 clears the filter.
         *
         * @param days - Days back value (1-90), or undefined to clear
         */
        setDaysBack: (days: number | undefined) => {
          if (days === undefined || days === 0) {
            set({ daysBack: undefined });
          } else {
            set({ daysBack: Math.min(Math.max(1, days), 90) });
          }
        },

        /**
         * Resets active filters to default state.
         * Clears saved filter selection and search text.
         */
        resetActiveFilters: () => {
          set({
            activeFilters: defaultFilters as unknown as ActiveFilters,
            savedFilterId: undefined,
            searchText: "",
            excludeRelists: false,
            excludeAuctions: false,
            isNewFilter: false,
            // daysBack and kurboMode are persisted per device; leaving them
            // out of reset made the button appear broken on devices where
            // they were set (results stayed constrained after "reset").
            daysBack: undefined,
            kurboMode: false,
          });
        },

        /**
         * Updates the active filter for a specific dropdown.
         * Syncs changes to the API if there's an active saved filter.
         *
         * @param dropdownName - The dropdown key to update (e.g., "brands", "vendors")
         * @param newSelection - New filter options for the dropdown
         * @param user - The authenticated user (optional)
         * @param savedFilters - User's saved filters from database
         * @param updateSavedFilter - API function to update the saved filter
         */
        updateActiveFilter: (
          dropdownName: string,
          newSelection: FilterOption[],
          user?: User,
          savedFilters?: DBSavedFilter[],
          updateSavedFilter?: (id: number, filter: SavedFilter) => void
        ) => {
          let updatedActiveFilters: ActiveFilters | undefined;
          let filterId: number | undefined;

          set((state) => {
            updatedActiveFilters = {
              ...state.activeFilters,
              [dropdownName]: newSelection,
            } as ActiveFilters;
            filterId = (state.activeFilters as ActiveFiltersWithId).id;
            return { activeFilters: updatedActiveFilters };
          });

          // Auto-sync only for newly created filters; existing filters require "SAVE CHANGES"
          if (
            !get().isImageSearchActive &&
            get().isNewFilter &&
            user &&
            filterId &&
            savedFilters &&
            updateSavedFilter &&
            updatedActiveFilters
          ) {
            const index = savedFilters.findIndex(
              (filter: DBSavedFilter) => filter?.search_filter?.id === filterId
            );

            if (index > -1) {
              const { searchText, excludeRelists, excludeAuctions, daysBack } = get();
              const updatedFilter = {
                ...savedFilters[index].search_filter,
                filters: updatedActiveFilters,
                searchText,
                excludeRelists: excludeRelists || undefined,
                excludeAuctions: excludeAuctions || undefined,
                daysBack: daysBack || undefined,
              } as SavedFilter;
              updateSavedFilter(savedFilters[index].id, updatedFilter);
            }
          }
        },

        /**
         * Removes a filter option from the active filters.
         * Special handling for currency/sort (reset to default) vs others (remove from array).
         * Syncs changes to the API if there's an active saved filter.
         *
         * @param option - The filter option to remove
         * @param dropdown - The dropdown type (affects removal logic)
         * @param user - The authenticated user (optional)
         * @param savedFilters - User's saved filters from database
         * @param updateSavedFilter - API function to update the saved filter
         */
        removeFilter: (
          option: FilterOption,
          dropdown?: string,
          user?: User,
          savedFilters?: DBSavedFilter[],
          updateSavedFilter?: (id: number, filter: SavedFilter) => void
        ) => {
          if (!option?.label) {
            return;
          }

          let updatedActiveFilters: ActiveFilters | undefined;
          let filterId: number | undefined;

          // If dropdown type is provided, use it to target the specific filter array
          if (dropdown) {
            if (dropdown === "size") {
              set((state) => {
                updatedActiveFilters = {
                  ...state.activeFilters,
                  size: state.activeFilters.size?.filter(
                    (selected: FilterOption) =>
                      selected.value !== option.value || selected.size !== option.size
                  ),
                } as ActiveFilters;
                filterId = (state.activeFilters as ActiveFiltersWithId).id;
                return { activeFilters: updatedActiveFilters };
              });
            } else if (dropdown === "currency") {
              // Reset currency to default (USD)
              set((state) => {
                updatedActiveFilters = {
                  ...state.activeFilters,
                  currency: [{ included: true, label: "USD", value: "USD" }] as FilterOption[],
                } as ActiveFilters;
                filterId = (state.activeFilters as ActiveFiltersWithId).id;
                return { activeFilters: updatedActiveFilters };
              });
            } else if (dropdown === "sort") {
              // Reset sort to default (Newest)
              set((state) => {
                updatedActiveFilters = {
                  ...state.activeFilters,
                  sort: [{ included: true, label: "Newest", value: "newest" }] as FilterOption[],
                } as ActiveFilters;
                filterId = (state.activeFilters as ActiveFiltersWithId).id;
                return { activeFilters: updatedActiveFilters };
              });
            } else {
              // Remove from the specific dropdown array
              set((state) => {
                updatedActiveFilters = {
                  ...state.activeFilters,
                  [dropdown]: state.activeFilters[dropdown as keyof ActiveFilters]?.filter(
                    (opt: FilterOption) => opt.value !== option.value
                  ),
                } as ActiveFilters;
                filterId = (state.activeFilters as ActiveFiltersWithId).id;
                return { activeFilters: updatedActiveFilters };
              });
            }

            // Auto-sync only for newly created filters; existing filters require "SAVE CHANGES"
            if (
              !get().isImageSearchActive &&
              get().isNewFilter &&
              user &&
              filterId &&
              savedFilters &&
              updateSavedFilter &&
              updatedActiveFilters
            ) {
              const index = savedFilters.findIndex(
                (filter: DBSavedFilter) => filter?.search_filter?.id === filterId
              );

              if (index > -1) {
                const { searchText, excludeRelists, excludeAuctions } = get();
                const updatedFilter = {
                  ...savedFilters[index].search_filter,
                  filters: updatedActiveFilters,
                  searchText,
                  excludeRelists: excludeRelists || undefined,
                  excludeAuctions: excludeAuctions || undefined,
                } as SavedFilter;
                updateSavedFilter(savedFilters[index].id, updatedFilter);
              }
            }
          }
        },

        /** Sets the free-form search text */
        setSearchText: (searchText: string) => set({ searchText }),

        /**
         * Updates the keyword search text.
         * Syncs changes to the API if there's an active saved filter.
         * Skips sync if the keyword hasn't actually changed.
         *
         * @param keyword - New search keyword
         * @param user - The authenticated user
         * @param savedFilters - User's saved filters from database
         * @param updateSavedFilter - API function to update the saved filter
         */
        updateKeywordSearch: (
          keyword: string | undefined,
          user: User,
          savedFilters: DBSavedFilter[],
          updateSavedFilter: (id: number, filter: SavedFilter) => void
        ) => {
          const { activeFilters } = get();

          // When called from URL preload (no user context), reset filters to avoid
          // previous session's filters bleeding into the new keyword search
          if (!user) {
            set({
              searchText: keyword || "",
              activeFilters: defaultFilters as unknown as ActiveFilters,
              savedFilterId: undefined,
            });
            return;
          }

          // When the user types a keyword while an existing (non-new) saved filter is active,
          // deselect the filter so the keyword applies to the conditions without modifying it.
          const { savedFilterId, isNewFilter } = get();
          if (keyword && savedFilterId && !isNewFilter) {
            set({ searchText: keyword, savedFilterId: undefined });
          } else {
            set({ searchText: keyword || "" });
          }

          const filterId = (activeFilters as ActiveFiltersWithId).id;
          if (!get().isImageSearchActive && get().isNewFilter && user && filterId) {
            const index = savedFilters?.findIndex(
              (filter: DBSavedFilter) => filter?.search_filter?.id === filterId
            );

            if (index > -1) {
              if (savedFilters[index].search_filter.searchText === keyword) {
                return;
              }

              const updatedFilter = {
                ...savedFilters[index].search_filter,
                searchText: keyword,
              };

              updateSavedFilter(savedFilters[index].id, updatedFilter);
            }
            // If filter not found, it may have been deleted - this is expected
          }
        },

        /**
         * Saves the current filters as a new or updated saved filter.
         * Creates a new filter if no existingId, otherwise updates existing.
         * Skips update if values haven't changed (deep equality check).
         *
         * @param filterName - Display name for the filter
         * @param existingId - ID of existing filter to update, or undefined to create new
         * @param savedFilters - User's saved filters from database
         * @param saveNewFilter - API function to create a new filter
         * @param updateSavedFilter - API function to update an existing filter
         */
        saveFilters: async (
          filterName: string,
          existingId: number | undefined,
          savedFilters: DBSavedFilter[],
          saveNewFilter: (filter: SavedFilter) => Promise<void>,
          updateSavedFilter: (id: number, filter: SavedFilter) => void
        ) => {
          const { activeFilters, searchText, excludeRelists, excludeAuctions, daysBack } = get();

          const id = existingId || new Date().getTime();

          if (existingId) {
            const index = savedFilters?.findIndex(
              (filter: DBSavedFilter) => filter?.search_filter?.id === existingId
            );

            if (index > -1) {
              const existing = savedFilters[index].search_filter;
              // Coerce to match the || undefined used when building the PUT payload,
              // otherwise false vs undefined mismatches after a server round-trip
              // cause the equality guard to fail and the save to loop.
              if (
                isEqual(existing.filters, activeFilters) &&
                existing.searchText === searchText &&
                (existing.excludeRelists || undefined) === (excludeRelists || undefined) &&
                (existing.excludeAuctions || undefined) === (excludeAuctions || undefined) &&
                (existing.daysBack || undefined) === (daysBack || undefined)
              ) {
                return;
              }

              const updatedFilter = {
                ...savedFilters[index].search_filter,
                filters: {
                  ...activeFilters,
                },
                searchText,
                excludeRelists: excludeRelists || undefined,
                excludeAuctions: excludeAuctions || undefined,
                daysBack: daysBack || undefined,
              } as unknown as SavedFilter;

              updateSavedFilter(savedFilters[index].id, updatedFilter);
            }
            return;
          }

          const newFilter = {
            id,
            name: filterName,
            searchText,
            filters: {
              ...activeFilters,
              id,
            },
            excludeRelists: excludeRelists || undefined,
            excludeAuctions: excludeAuctions || undefined,
            daysBack: daysBack || undefined,
          } as unknown as SavedFilter;

          // Awaited (unlike updateSavedFilter below) because saveNewFilter
          // re-throws on failure and callers such as SearchRescue's quick-save
          // rely on that to distinguish a real success from a failed request
          // before showing a persistent "SEARCH SAVED" state.
          await saveNewFilter(newFilter);
          set({
            activeFilters: newFilter.filters as unknown as ActiveFilters,
            isNewFilter: true,
            savedFilterId: undefined,
          });
        },

        /**
         * Selects a saved filter by ID and applies its filters.
         * Can search by search_filter.id or database id.
         * Resets to default filters if filterId is falsy or not found.
         *
         * @param filterId - The filter ID to select (undefined to reset)
         * @param dbId - If true, searches by db id; otherwise search_filter.id
         * @param savedFilters - User's saved filters from database
         */
        selectSavedFilter: (
          filterId?: number,
          dbId: boolean = false,
          savedFilters?: DBSavedFilter[]
        ) => {
          if (!filterId) {
            actions.resetActiveFilters();
            return;
          }

          const filter = savedFilters?.find(
            (filter: DBSavedFilter) =>
              (dbId ? filter?.id : filter?.search_filter?.id?.toString()) == filterId.toString()
          );

          if (!filter) {
            actions.resetActiveFilters();
            return;
          }

          set({
            activeFilters: (isValidActiveFilters(filter.search_filter.filters)
              ? filter.search_filter.filters
              : defaultFilters) as unknown as ActiveFilters,
            savedFilterId: filter?.id,
            searchText: filter.search_filter.searchText || "",
            excludeRelists: filter.search_filter.excludeRelists ?? false,
            excludeAuctions: filter.search_filter.excludeAuctions ?? false,
            daysBack: filter.search_filter.daysBack ?? undefined,
            isNewFilter: false,
          });
        },

        /**
         * Applies a filter shared by another user via a public share link
         * (?share=<token> → GET /api/v2/filters/shared/:token). Unlike
         * selectSavedFilter, this never sets savedFilterId — the recipient
         * doesn't own the row it came from, so it must not be treated as an
         * existing saved filter they could unknowingly overwrite.
         *
         * @param searchFilter - The shared filter's criteria, same shape as
         *   a saved filter's own `search_filter` column
         */
        applySharedFilter: (searchFilter: SavedFilter) => {
          const incomingFilters = isValidActiveFilters(searchFilter.filters)
            ? searchFilter.filters
            : defaultFilters;

          set((state) => ({
            activeFilters: {
              ...incomingFilters,
              // Currency is the recipient's own regional/display preference,
              // not part of what the sender meant to share — keep whatever
              // this browser already had instead of switching it to the
              // sender's (overwrites whatever incomingFilters.currency was).
              currency: state.activeFilters.currency,
            } as unknown as ActiveFilters,
            savedFilterId: undefined,
            searchText: searchFilter.searchText || "",
            excludeRelists: searchFilter.excludeRelists ?? false,
            excludeAuctions: searchFilter.excludeAuctions ?? false,
            daysBack: searchFilter.daysBack ?? undefined,
            isNewFilter: false,
          }));
        },

        /**
         * Deletes a saved filter and resets active filters.
         *
         * @param filterId - The filter ID to delete
         * @param deleteSavedFilter - API function to delete the filter
         */
        handleDeleteSavedFilter: (filterId?: number, deleteSavedFilter?: (id: number) => void) => {
          if (!filterId || !deleteSavedFilter) {
            return;
          }

          deleteSavedFilter(filterId);
          actions.resetActiveFilters();
        },

        /** Sets the list of saved filters from the database */
        setSavedFilters: (filters: DBSavedFilter[]) => set({ savedFilters: filters }),

        /** Sets the active saved filter ID without resetting isNewFilter */
        setSavedFilterId: (id: number | undefined) => set({ savedFilterId: id }),

        /** Tracks whether Kurb Lens is active to prevent saved filter sync during image search */
        setImageSearchActive: (active: boolean) => set({ isImageSearchActive: active }),

        /**
         * Preloads filters for a specific brand from URL/route.
         * Used when navigating directly to a brand page.
         * Resets to default filters if brand is not found.
         *
         * @param brand - Brand label to search for
         * @param brands - All available brands
         */
        setPreloadBrand: (brand: string, brands: BrandFilter[]) => {
          const isBrand = brands?.find((b: BrandFilter) => b.label === brand);

          if (!isBrand) {
            set({
              savedFilterId: undefined,
              isNewFilter: false,
              activeFilters: defaultFilters as unknown as ActiveFilters,
            });
            return;
          }

          set({
            searchText: "",
            savedFilterId: undefined,
            isNewFilter: false,
            activeFilters: {
              ...defaultFilters,
              brands: [
                {
                  included: true,
                  label: isBrand.label,
                  value: isBrand.id,
                  state: "include",
                  timestamp: new Date().getTime(),
                },
              ] as FilterOption[],
            } as unknown as ActiveFilters,
          });
        },

        /**
         * Preloads filters for a specific vendor from URL/route.
         * Used when navigating directly to a vendor page (case-insensitive search).
         * Resets to default filters if vendor is not found.
         *
         * @param vendor - Vendor label to search for
         * @param vendors - All available vendors
         */
        setPreloadVendor: (vendor: string, vendors: VendorFilter[]) => {
          const isVendor = vendors?.find(
            (v: VendorFilter) => v.label.toLowerCase() === vendor.toLowerCase()
          );

          if (!isVendor) {
            set({
              savedFilterId: undefined,
              isNewFilter: false,
              activeFilters: defaultFilters as unknown as ActiveFilters,
            });
            return;
          }

          set({
            searchText: "",
            savedFilterId: undefined,
            isNewFilter: false,
            activeFilters: {
              ...defaultFilters,
              vendors: [
                {
                  included: true,
                  label: isVendor.label,
                  value: isVendor.id,
                  state: "include",
                  timestamp: new Date().getTime(),
                },
              ] as FilterOption[],
            } as unknown as ActiveFilters,
          });
        },

        /**
         * Sets a type filter while preserving other active filters.
         * Used when navigating to a type-specific page.
         *
         * @param typeValue - The type value to apply (e.g., "footwear")
         */
        setPreloadedType: (typeValue: string) => {
          const selectedType = typeSelection.find((t) => t.value === typeValue);

          if (!selectedType) {
            set({
              savedFilterId: undefined,
              isNewFilter: false,
              activeFilters: defaultFilters as unknown as ActiveFilters,
            });
            return;
          }

          set({
            searchText: "",
            savedFilterId: undefined,
            activeFilters: {
              ...defaultFilters,
              type: [
                {
                  included: true,
                  label: selectedType.label,
                  value: selectedType.value,
                  state: "include" as const,
                  timestamp: new Date().getTime(),
                },
              ] as FilterOption[],
            } as unknown as ActiveFilters,
          });
        },

        /**
         * Applies preloaded filters from URL search parameters.
         * Reads brands, vendors, and regions from window.location.search.
         * Clears URL params after applying and sends GA event for vendor filters.
         *
         * @param brands - All available brands
         * @param vendors - All available vendors
         */
        setPreloadedFilters: (brands: BrandFilter[], vendors: VendorFilter[]) => {
          const searchParams = new URLSearchParams(window.location.search);
          const filteredBrands = searchParams.getAll("brands");
          const filteredRegions = searchParams.getAll("regions");
          const filteredVendors = searchParams.getAll("vendors");

          if (filteredBrands.length || filteredRegions.length || filteredVendors.length) {
            const countries = countrySelection.filter(
              (country) =>
                (country.region && filteredRegions.includes(country.region)) ||
                filteredRegions.includes(country.value as string)
            );

            const selectedBrand = brands?.filter(
              (brand) =>
                filteredBrands.includes(brand.id.toString()) ||
                filteredBrands.some(
                  (fb) => decodeURIComponent(fb).toLowerCase() === brand.label.toLowerCase()
                )
            );

            const selectedVendors = vendors?.filter(
              (vendor) =>
                filteredVendors.includes(vendor.id.toString()) ||
                filteredVendors.some(
                  (fv) => decodeURIComponent(fv).toLowerCase() === vendor.label.toLowerCase()
                )
            );

            if (selectedVendors?.length) {
              sendGtagEvent("click", "home", "drops_banner");
            }

            set({
              searchText: "",
              savedFilterId: undefined,
              isNewFilter: false,
              activeFilters: {
                ...defaultFilters,
                brands:
                  selectedBrand?.map((brand) => ({
                    included: true,
                    label: brand.label,
                    value: brand.id,
                    state: "include",
                  })) || [],
                regions: countries.map((country) => ({
                  included: true,
                  label: country.label,
                  value: country.value,
                  state: "include",
                })),
                vendors:
                  selectedVendors?.map((vendor) => ({
                    included: true,
                    label: vendor.label,
                    id: vendor.id,
                    value: vendor.id,
                    state: "include",
                  })) || [],
              } as unknown as ActiveFilters,
            });

            if (selectedBrand?.length || countries?.length || selectedVendors?.length) {
              const url = new URL(window.location.href);
              url.search = "";
              window.history.replaceState(null, "", url);
            }
          }
        },
      };

      return { actions };
    }),
    {
      name: "filters-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeFilters: state.activeFilters,
        savedFilterId: state.savedFilterId,
        searchText: state.searchText,
        excludeRelists: state.excludeRelists,
        excludeAuctions: state.excludeAuctions,
        daysBack: state.daysBack,
        kurboMode: state.kurboMode,
      }),
    }
  )
);
